import React from 'react';
import SvgLoader from 'src/components/Common/SvgLoader';
import { useDebounce } from 'src/lib/Hooks/useDebounce';
import { CarouselContextProvider, useCarouselContext } from 'src/lib/Utils/Contexts/Carousel';
import { CarouselButtonsType, CarouselSlideType, CarouselType } from './types';

const CarouselButtons = ({
  arrowBelow = false,
  arrowColor = 'text-teal-darker',
  arrowSize = 30,
  delay = 300,
  totalSlides,
}: CarouselButtonsType) => {
  const { dispatch, state } = useCarouselContext();
  const { activeSlide } = state;
  const lastSlideIndex = totalSlides - 1;
  const baseClasses = 'absolute transform md:top-1/2 md:-translate-y-1/2';
  const arrowPosClass = arrowBelow ? 'bottom-0 z-10' : 'top-0 -translate-y-64';

  // it just 'feels' better (tm) with the useDebounce timeout delay being slightly
  // offset from the css animation duration
  const computedDelay = delay - 100;

  // move forward normally
  const nextSetSlide = useDebounce(
    () => dispatch({ type: 'setNextSlide', payload: { moveSlide: 1, slideDirection: 'forward' } }),
    computedDelay
  );

  // move forward by resetting the slides by starting again at the first index
  const nextSetSlideIndex = useDebounce(
    () =>
      dispatch({
        type: 'setSlideIndex',
        payload: { slideDirection: 'forward', slideIndex: 0 },
      }),
    computedDelay
  );

  // move backward normally
  const previousSetSlide = useDebounce(
    () =>
      dispatch({
        type: 'setNextSlide',
        payload: { moveSlide: -1, slideDirection: 'backward' },
      }),
    computedDelay
  );

  // move backward by resetting the slides by starting at the last index
  const previousSetSlideIndex = useDebounce(
    () =>
      dispatch({
        type: 'setSlideIndex',
        payload: { slideDirection: 'backward', slideIndex: lastSlideIndex },
      }),
    computedDelay
  );

  return (
    <>
      <button
        aria-label="previous slide"
        className={`${baseClasses} ${arrowPosClass} left-0 pl-16 md:pl-0`}
        data-testid="carousel-previous"
        onClick={activeSlide === 0 ? previousSetSlideIndex : previousSetSlide}
      >
        <SvgLoader color={arrowColor} name="Arrow" rotate={180} size={arrowSize} />
      </button>
      <button
        aria-label="next slide"
        className={`${baseClasses} ${arrowPosClass} right-0 pr-16 md:pr-0`}
        data-testid="carousel-next"
        onClick={activeSlide === lastSlideIndex ? nextSetSlideIndex : nextSetSlide}
      >
        <SvgLoader color={arrowColor} name="Arrow" size={arrowSize} />
      </button>
    </>
  );
};

const Carousel = ({ children, controls, onChange }: CarouselType) => {
  const totalSlides = React.Children.count(children);

  // We need to pass the buttons through as a prop because we don't want to include them
  // as children and affect the sliding
  return (
    <CarouselContextProvider
      buttons={
        <CarouselButtons
          arrowBelow={controls?.arrowBelow}
          arrowColor={controls?.arrowColor}
          arrowSize={controls?.arrowSize}
          delay={controls?.delay}
          {...{ totalSlides }}
        />
      }
      {...{ controls, totalSlides, onChange }}
    >
      {children}
    </CarouselContextProvider>
  );
};

const CarouselSlide = ({ children, className }: CarouselSlideType) => (
  <li
    className={`js-carousel-slide absolute transition-all h-full w-full ${className}`}
    style={{ transitionTimingFunction: 'cubic-bezier(0.785, 0.135, 0.150, 0.860)' }}
  >
    <div className="flex h-full w-full justify-center">{children}</div>
  </li>
);

export { CarouselSlide };
export default Carousel;
